import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { User, CreateTenantRequest } from '@tco/shared-types'
import AuthService from '../services/AuthService'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  /** WebView мобилки: обмен одноразового кода на сессию */
  loginWithWebviewCode: (code: string) => Promise<User>
  register: (registrationData: CreateTenantRequest) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const authService = AuthService.getInstance()

  useEffect(() => {
    // Initialize auth state
    const initAuth = async () => {
      // Only restore session from localStorage
      const currentUser = authService.getUser()
      if (currentUser) {
        setUser(currentUser)
      }
      setIsLoading(false)
    }

    void initAuth()

    // Start auto-refresh for tokens
    const stopAutoRefresh = authService.startAutoRefresh()

    return () => {
      stopAutoRefresh()
    }
  }, [])

  const login = async (email: string, password: string) => {
    const loggedInUser = await authService.login({ email, password })
    setUser(loggedInUser)
  }

  const loginWithWebviewCode = async (code: string) => {
    const webviewUser = await authService.exchangeWebviewCode(code)
    setUser(webviewUser)
    return webviewUser
  }

  const register = async (registrationData: CreateTenantRequest) => {
    const registeredUser = await authService.register(registrationData)
    setUser(registeredUser)
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
  }

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithWebviewCode,
    register,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}